import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, X, FileText, BarChart3, Newspaper, MessageSquareQuote, ArrowUp, Building2, LayoutTemplate, TrendingUp, TrendingDown } from 'lucide-react';
import { Avatar } from './ui/avatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis } from 'recharts';
import { articles, rassegne, sources, reports, tierLabels } from '../lib/data';
const suggestions = [{
  icon: FileText,
  title: 'Genera un report',
  description: 'Crea un report mensile per il cliente con i dati più recenti'
}, {
  icon: BarChart3,
  title: 'Analizza le statistiche',
  description: 'Mostrami un riepilogo delle performance di copertura mediatica'
}, {
  icon: Newspaper,
  title: 'Riassumi la rassegna',
  description: 'Genera un executive summary della rassegna di oggi'
}, {
  icon: MessageSquareQuote,
  title: 'Confronta i periodi',
  description: 'Confronta la copertura di questo mese con il mese precedente'
}];
type Widget = {
  type: 'kpis';
  items: {
    label: string;
    value: string;
    delta?: string;
    deltaPositive?: boolean;
  }[];
} | {
  type: 'sentiment';
  positive: number;
  neutral: number;
  negative: number;
} | {
  type: 'list';
  title: string;
  items: {
    label: string;
    value: string;
    sub?: string;
  }[];
} | {
  type: 'chart';
  title: string;
  data: {
    date: string;
    value: number;
  }[];
} | {
  type: 'comparison';
  items: {
    label: string;
    current: string;
    previous: string;
    delta: string;
    positive: boolean;
  }[];
};
type Message = {
  id: string;
  role: 'user' | 'ai';
  content: string;
  isTyping?: boolean;
  widgets?: Widget[];
};
type AiResponse = {
  text: string;
  widgets?: Widget[];
};
function getSimulatedResponse(input: string): AiResponse {
  const lowerInput = input.toLowerCase();
  if (lowerInput.includes('report') || lowerInput.includes('genera')) {
    return {
      text: 'Ho generato il report mensile per Intrum relativo al periodo 1-20 Aprile 2026. Ecco un riepilogo:\n\nI temi principali riguardano la gestione dei crediti deteriorati (NPL), le operazioni di cartolarizzazione e il ruolo di Intrum nel mercato italiano del credit management. Il report è pronto per il download nella sezione Report.',
      widgets: [{
        type: 'kpis',
        items: [{
          label: 'Articoli',
          value: '20',
          delta: '+25%',
          deltaPositive: true
        }, {
          label: 'Score medio',
          value: '7.4',
          delta: '+8.8%',
          deltaPositive: true
        }, {
          label: 'AVE totale',
          value: '€187k',
          delta: '+31.7%',
          deltaPositive: true
        }, {
          label: 'Testate uniche',
          value: '12',
          delta: '+2',
          deltaPositive: true
        }]
      }, {
        type: 'sentiment',
        positive: 65,
        neutral: 25,
        negative: 10
      }]
    };
  }
  if (lowerInput.includes('statistiche') || lowerInput.includes('analizza') || lowerInput.includes('performance')) {
    return {
      text: 'Ecco il riepilogo delle performance di copertura mediatica per Intrum.\n\nLa visibilità è particolarmente forte nel segmento finanziario specializzato, con un focus crescente su NPL e strategie di servicing.',
      widgets: [{
        type: 'kpis',
        items: [{
          label: 'Articoli',
          value: '20'
        }, {
          label: 'Testate Tier 1',
          value: '5'
        }, {
          label: 'Autori unici',
          value: '14'
        }, {
          label: 'In prima pagina',
          value: '4'
        }]
      }, {
        type: 'list',
        title: 'Top testate',
        items: [{
          label: 'Il Sole 24 Ore',
          value: '4 art.',
          sub: 'Score 8.5'
        }, {
          label: 'MF Milano Finanza',
          value: '3 art.',
          sub: 'Score 8.1'
        }, {
          label: 'Corriere della Sera',
          value: '2 art.',
          sub: 'Score 7.8'
        }, {
          label: 'La Repubblica',
          value: '2 art.',
          sub: 'Score 7.2'
        }]
      }]
    };
  }
  if (lowerInput.includes('riassumi') || lowerInput.includes('rassegna') || lowerInput.includes('summary') || lowerInput.includes('executive')) {
    return {
      text: "Ecco l'executive summary della rassegna di oggi:\n\n**Intrum nella stampa italiana — Aprile 2026**\n\nLa copertura è dominata da tre filoni: operazioni NPL (acquisizione portafoglio €1.2 mld da UniCredit), strategia italiana di espansione, e posizionamento competitivo nel mercato del credit management. Il sentiment è prevalentemente positivo.",
      widgets: [{
        type: 'sentiment',
        positive: 65,
        neutral: 25,
        negative: 10
      }, {
        type: 'chart',
        title: 'Andamento articoli — ultimi 15 giorni',
        data: [{
          date: '1',
          value: 1
        }, {
          date: '2',
          value: 2
        }, {
          date: '3',
          value: 1
        }, {
          date: '4',
          value: 0
        }, {
          date: '5',
          value: 2
        }, {
          date: '6',
          value: 1
        }, {
          date: '7',
          value: 2
        }, {
          date: '8',
          value: 3
        }, {
          date: '9',
          value: 1
        }, {
          date: '10',
          value: 2
        }, {
          date: '11',
          value: 1
        }, {
          date: '12',
          value: 0
        }, {
          date: '13',
          value: 2
        }, {
          date: '14',
          value: 1
        }, {
          date: '15',
          value: 1
        }]
      }]
    };
  }
  if (lowerInput.includes('confronta') || lowerInput.includes('confronto') || lowerInput.includes('periodo') || lowerInput.includes('mese')) {
    return {
      text: "Ecco il confronto tra la copertura di questo mese e il mese precedente.\n\n**Trend positivo**: la copertura cresce sia in volume che in qualità. L'aumento delle uscite Tier 1 (+66.7%) è particolarmente significativo e riflette il maggiore interesse delle testate di riferimento.",
      widgets: [{
        type: 'comparison',
        items: [{
          label: 'Articoli',
          current: '20',
          previous: '16',
          delta: '+25%',
          positive: true
        }, {
          label: 'Score medio',
          current: '7.4',
          previous: '6.8',
          delta: '+8.8%',
          positive: true
        }, {
          label: 'AVE totale',
          current: '€187k',
          previous: '€142k',
          delta: '+31.7%',
          positive: true
        }, {
          label: 'Tier 1',
          current: '5',
          previous: '3',
          delta: '+66.7%',
          positive: true
        }, {
          label: 'Sentiment positivo',
          current: '65%',
          previous: '56%',
          delta: '+9pp',
          positive: true
        }]
      }]
    };
  }
  return {
    text: 'Ho analizzato la tua richiesta. Sulla base dei dati disponibili per Intrum, la copertura mediatica nel periodo corrente mostra un trend positivo. Vuoi che approfondisca qualche aspetto specifico?',
    widgets: [{
      type: 'kpis',
      items: [{
        label: 'Articoli',
        value: '20'
      }, {
        label: 'Score medio',
        value: '7.4'
      }, {
        label: 'Sentiment +',
        value: '65%'
      }]
    }]
  };
}
function formatText(text: string) {
  return text.split('\n').map((line, i) => {
    if (line === '') return <br key={i} data-id="element-264" />;
    const parts = line.split(/(\*\*.*?\*\*)/g);
    return <div key={i} className="min-h-[1.5em]" data-id="element-265">
        {parts.map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j} className="font-semibold text-foreground" data-id="element-266">
                {part.slice(2, -2)}
              </strong>;
        }
        return <span key={j} data-id="element-267">{part}</span>;
      })}
      </div>;
  });
}
function TypewriterText({
  text,
  onComplete
}: {
  text: string;
  onComplete: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
  useEffect(() => {
    if (currentIndex < words.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + words[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 40); // ~40ms per word/token
      return () => clearTimeout(timeout);
    } else {
      onComplete();
    }
  }, [currentIndex, words, onComplete]);
  return <>{formatText(displayedText)}</>;
}
function WidgetKpis({
  items
}: {
  items: {
    label: string;
    value: string;
    delta?: string;
    deltaPositive?: boolean;
  }[];
}) {
  return <div className={`grid gap-2 ${items.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : items.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} data-id="element-268">
      {items.map((item, i) => <div key={i} className="border border-border rounded-xl p-3 bg-card" data-id="element-269">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5" data-id="element-270">
            {item.label}
          </div>
          <div className="text-xl font-light text-foreground leading-none" style={{
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-271">
            {item.value}
          </div>
          {item.delta && <div className={`text-[11px] mt-1.5 flex items-center gap-0.5 ${item.deltaPositive ? 'text-emerald-600' : 'text-destructive'}`} data-id="element-272">
              {item.deltaPositive ? <TrendingUp className="h-3 w-3" data-id="element-273" /> : <TrendingDown className="h-3 w-3" data-id="element-274" />}
              <span style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-275">
                {item.delta}
              </span>
            </div>}
        </div>)}
    </div>;
}
function WidgetSentiment({
  positive,
  neutral,
  negative
}: {
  positive: number;
  neutral: number;
  negative: number;
}) {
  return <div className="border border-border rounded-xl p-4 bg-card" data-id="element-276">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-277">
        Sentiment
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden mb-3" data-id="element-278">
        <div className="bg-accent" style={{
        width: `${positive}%`
      }} data-id="element-279" />
        <div className="bg-[#64B5F6]" style={{
        width: `${neutral}%`
      }} data-id="element-280" />
        <div className="bg-[#1A3A5C]" style={{
        width: `${negative}%`
      }} data-id="element-281" />
      </div>
      <div className="flex items-center justify-between text-xs" data-id="element-282">
        <div className="flex items-center gap-1.5" data-id="element-283">
          <span className="w-2 h-2 rounded-full bg-accent" data-id="element-284" />
          <span className="text-muted-foreground" data-id="element-285">Positivo</span>
          <span className="font-medium text-foreground" style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-286">
            {positive}%
          </span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-287">
          <span className="w-2 h-2 rounded-full bg-[#64B5F6]" data-id="element-288" />
          <span className="text-muted-foreground" data-id="element-289">Neutro</span>
          <span className="font-medium text-foreground" style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-290">
            {neutral}%
          </span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-291">
          <span className="w-2 h-2 rounded-full bg-[#1A3A5C]" data-id="element-292" />
          <span className="text-muted-foreground" data-id="element-293">Negativo</span>
          <span className="font-medium text-foreground" style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-294">
            {negative}%
          </span>
        </div>
      </div>
    </div>;
}
function WidgetList({
  title,
  items
}: {
  title: string;
  items: {
    label: string;
    value: string;
    sub?: string;
  }[];
}) {
  return <div className="border border-border rounded-xl bg-card overflow-hidden" data-id="element-295">
      <div className="px-4 py-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium border-b border-border" data-id="element-296">
        {title}
      </div>
      <div className="divide-y divide-border" data-id="element-297">
        {items.map((item, i) => <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm" data-id="element-298">
            <span className="text-foreground font-medium truncate pr-3" data-id="element-299">
              {item.label}
            </span>
            <div className="text-right flex-shrink-0" data-id="element-300">
              <div className="text-foreground" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-301">
                {item.value}
              </div>
              {item.sub && <div className="text-[11px] text-muted-foreground" data-id="element-302">
                  {item.sub}
                </div>}
            </div>
          </div>)}
      </div>
    </div>;
}
function WidgetChart({
  title,
  data
}: {
  title: string;
  data: {
    date: string;
    value: number;
  }[];
}) {
  return <div className="border border-border rounded-xl p-4 bg-card" data-id="element-303">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-304">
        {title}
      </div>
      <div className="h-32" data-id="element-305">
        <ResponsiveContainer width="100%" height="100%" data-id="element-306">
          <AreaChart data={data} margin={{
          top: 5,
          right: 5,
          left: 5,
          bottom: 0
        }} data-id="element-307">
            <defs data-id="element-308">
              <linearGradient id="aiChartGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-309">
                <stop offset="0%" stopColor="#0187c3" stopOpacity={0.4} data-id="element-310" />
                <stop offset="100%" stopColor="#0187c3" stopOpacity={0} data-id="element-311" />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide data-id="element-312" />
            <RechartsTooltip contentStyle={{
            fontSize: 11,
            borderRadius: 8,
            border: '1px solid var(--border)'
          }} labelStyle={{
            color: 'var(--muted-foreground)'
          }} data-id="element-313" />
            <Area type="monotone" dataKey="value" stroke="#0187c3" strokeWidth={2} fill="url(#aiChartGrad)" data-id="element-314" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
}
function WidgetComparison({
  items
}: {
  items: {
    label: string;
    current: string;
    previous: string;
    delta: string;
    positive: boolean;
  }[];
}) {
  return <div className="border border-border rounded-xl bg-card overflow-hidden" data-id="element-315">
      <div className="px-4 py-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium border-b border-border" data-id="element-316">
        Mese corrente vs precedente
      </div>
      <div className="divide-y divide-border" data-id="element-317">
        {items.map((item, i) => <div key={i} className="px-4 py-3 flex items-center justify-between gap-3" data-id="element-318">
            <span className="text-sm text-foreground font-medium" data-id="element-319">
              {item.label}
            </span>
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-320">
              <div className="text-right" data-id="element-321">
                <div className="text-base font-light text-foreground leading-none" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-322">
                  {item.current}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-323">
                  prev. {item.previous}
                </div>
              </div>
              <div className={`text-xs font-medium px-2 py-1 rounded-md flex items-center gap-0.5 ${item.positive ? 'text-emerald-600 bg-emerald-50' : 'text-destructive bg-red-50'}`} style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-324">
                {item.positive ? <TrendingUp className="h-3 w-3" data-id="element-325" /> : <TrendingDown className="h-3 w-3" data-id="element-326" />}
                {item.delta}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}
function WidgetRenderer({
  widgets
}: {
  widgets: Widget[];
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="mt-4 space-y-3" data-id="element-327">
      {widgets.map((w, i) => {
      if (w.type === 'kpis') return <WidgetKpis key={i} items={w.items} data-id="element-328" />;
      if (w.type === 'sentiment') return <WidgetSentiment key={i} positive={w.positive} neutral={w.neutral} negative={w.negative} data-id="element-329" />;
      if (w.type === 'list') return <WidgetList key={i} title={w.title} items={w.items} data-id="element-330" />;
      if (w.type === 'chart') return <WidgetChart key={i} title={w.title} data={w.data} data-id="element-331" />;
      if (w.type === 'comparison') return <WidgetComparison key={i} items={w.items} data-id="element-332" />;
      return null;
    })}
    </motion.div>;
}
function AIModeOverlay({
  onClose
}: {
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<'welcome' | 'chat'>('welcome');
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isAiTyping]);
  const handleSubmit = (text: string) => {
    if (!text.trim() || isAiTyping) return;
    if (phase === 'welcome') {
      setPhase('chat');
    }
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsAiTyping(true);
    // Simulate network delay before starting to type
    setTimeout(() => {
      const aiResponse = getSimulatedResponse(text);
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: aiResponse.text,
        widgets: aiResponse.widgets,
        isTyping: true
      };
      setMessages(prev => [...prev, aiMsg]);
    }, 600);
  };
  const handleAiComplete = (id: string) => {
    setMessages(prev => prev.map(m => m.id === id ? {
      ...m,
      isTyping: false
    } : m));
    setIsAiTyping(false);
  };
  return <motion.div key="ai-overlay" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: 'easeInOut'
  }} className="fixed inset-0 bg-white flex flex-col" style={{
    zIndex: 99999
  }} data-id="element-333">
      {/* Top bar */}
      <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-334">
        <button type="button" onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" style={{
        zIndex: 100000
      }} aria-label="Chiudi AI Mode" data-id="element-335">
          <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-336" />
        </button>

        <div className="flex items-center gap-3" data-id="element-337">
          <span className="font-medium text-sm text-foreground" data-id="element-338">AI Mode</span>
        </div>

        <div className="flex items-center gap-2" data-id="element-339">
          <Avatar className="h-9 w-9" data-id="element-340">
            <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-341" />
          </Avatar>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-342">
        <AnimatePresence mode="wait" data-id="element-343">
          {phase === 'welcome' ? <motion.div key="welcome-phase" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="flex-1 flex flex-col items-center justify-center px-6 -mt-16 overflow-y-auto" data-id="element-344">
              <div className="w-full max-w-2xl space-y-3 mb-10" data-id="element-345">
                <div className="flex items-center gap-2 justify-center mb-6" data-id="element-346">
                  <h2 className="text-2xl font-semibold text-foreground" data-id="element-347">
                    Come posso aiutarti?
                  </h2>
                </div>

                {/* Search field */}
                <div className="relative" data-id="element-348">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" data-id="element-349" />
                  <input type="text" autoFocus value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit(inputValue);
                }
              }} placeholder="Chiedi qualcosa all'AI..." className="w-full pl-14 pr-6 py-4 text-lg border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-shadow hover:shadow-md" data-id="element-350" />
                </div>
              </div>

              {/* Suggestion boxes */}
              <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-351">
                {suggestions.map((s, i) => {
              const Icon = s.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: 0.1 + i * 0.07,
                ease: 'easeOut'
              }} onClick={() => handleSubmit(s.title)} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-white hover:border-accent/40 hover:bg-accent/5 transition-all text-left group cursor-pointer" data-id="element-352">
                      <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors" data-id="element-353">
                        <Icon className="h-4 w-4 text-accent" data-id="element-354" />
                      </div>
                      <div className="min-w-0" data-id="element-355">
                        <div className="text-sm font-medium text-foreground mb-0.5" data-id="element-356">
                          {s.title}
                        </div>
                        <div className="text-xs text-muted-foreground leading-relaxed" data-id="element-357">
                          {s.description}
                        </div>
                      </div>
                    </motion.div>;
            })}
              </div>
            </motion.div> : <motion.div key="chat-phase" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="flex-1 flex flex-col h-full" data-id="element-358">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-359">
                <div className="max-w-2xl mx-auto space-y-8" data-id="element-360">
                  {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-361">
                      {msg.role === 'ai' && <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mr-4 mt-1" data-id="element-362">
                          <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-363" />
                        </div>}

                      <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-accent text-white rounded-2xl rounded-br-md px-5 py-3.5 text-[15px] leading-relaxed shadow-sm' : 'text-foreground text-[15px] leading-relaxed pt-1 w-full'}`} data-id="element-364">
                        {msg.role === 'ai' ? <div data-id="element-365">
                            <div className="text-muted-foreground" data-id="element-366">
                              {msg.isTyping ? <TypewriterText text={msg.content} onComplete={() => handleAiComplete(msg.id)} data-id="element-367" /> : formatText(msg.content)}
                            </div>
                            {!msg.isTyping && msg.widgets && msg.widgets.length > 0 && <WidgetRenderer widgets={msg.widgets} data-id="element-368" />}
                          </div> : msg.content}
                      </div>
                    </div>)}

                  {isAiTyping && !messages.find(m => m.isTyping) && <div className="flex justify-start" data-id="element-369">
                      <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mr-4 mt-1" data-id="element-370">
                        <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-371" />
                      </div>
                      <div className="pt-2.5 flex gap-1" data-id="element-372">
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0
                  }} data-id="element-373" />
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.2
                  }} data-id="element-374" />
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.4
                  }} data-id="element-375" />
                      </div>
                    </div>}
                  <div ref={messagesEndRef} className="h-4" data-id="element-376" />
                </div>
              </div>

              {/* Chat Input Area */}
              <div className="bg-white border-t border-border p-4 flex-shrink-0" data-id="element-377">
                <div className="max-w-2xl mx-auto relative" data-id="element-378">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit(inputValue);
                }
              }} placeholder="Scrivi un messaggio..." className="w-full pl-5 pr-12 py-3.5 text-[15px] border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-shadow" disabled={isAiTyping} data-id="element-379" />
                  <button onClick={() => handleSubmit(inputValue)} disabled={!inputValue.trim() || isAiTyping} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-opacity" data-id="element-380">
                    <ArrowUp className="h-4 w-4" data-id="element-381" />
                  </button>
                </div>
                <div className="text-center mt-2" data-id="element-382">
                  <span className="text-[11px] text-muted-foreground" data-id="element-383">
                    L'AI può commettere errori. Verifica sempre le informazioni
                    importanti.
                  </span>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
function SearchOverlay({
  onClose
}: {
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const lowerQuery = query.toLowerCase();
  const isSearching = query.trim().length > 0;
  const filteredArticles = isSearching ? articles.filter(a => a.title.toLowerCase().includes(lowerQuery) || a.source.toLowerCase().includes(lowerQuery) || a.author.toLowerCase().includes(lowerQuery)) : articles.slice(0, 3);
  const filteredRassegne = isSearching ? rassegne.filter(r => r.name.toLowerCase().includes(lowerQuery) || r.provider.toLowerCase().includes(lowerQuery)) : rassegne.slice(0, 3);
  const filteredSources = isSearching ? sources.filter(s => s.name.toLowerCase().includes(lowerQuery)) : sources.slice(0, 3);
  const filteredReports = isSearching ? reports.filter(r => r.name.toLowerCase().includes(lowerQuery)) : reports.slice(0, 3);
  const hasResults = filteredArticles.length > 0 || filteredRassegne.length > 0 || filteredSources.length > 0 || filteredReports.length > 0;
  return <motion.div key="search-overlay" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} className="fixed inset-0 bg-white flex flex-col" style={{
    zIndex: 99999
  }} data-id="element-384">
      {/* Top bar */}
      <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-385">
        <button type="button" onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" aria-label="Chiudi Ricerca" data-id="element-386">
          <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-387" />
        </button>

        <div className="flex items-center gap-3" data-id="element-388">
          <span className="font-medium text-sm text-foreground" data-id="element-389">Ricerca</span>
        </div>

        <div className="flex items-center gap-2" data-id="element-390">
          <span className="text-sm text-muted-foreground mr-1" data-id="element-391">Marco C.</span>
          <Avatar className="h-9 w-9" data-id="element-392">
            <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-393" />
          </Avatar>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto bg-background flex flex-col items-center pt-12 pb-24 px-6" data-id="element-394">
        <div className="w-full max-w-2xl" data-id="element-395">
          {/* Search Input */}
          <div className="relative mb-12" data-id="element-396">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground" data-id="element-397" />
            <input ref={inputRef} type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca articoli, rassegne, testate, report..." className="w-full pl-16 pr-6 py-5 text-xl border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-shadow hover:shadow-md" data-id="element-398" />
          </div>

          {/* Results Area */}
          {!hasResults ? <div className="text-center py-16" data-id="element-399">
              <Search className="h-8 w-8 text-muted-foreground mx-auto mb-3 opacity-50" data-id="element-400" />
              <p className="text-sm text-muted-foreground" data-id="element-401">
                Nessun risultato trovato per "{query}"
              </p>
            </div> : <div className="space-y-10" data-id="element-402">
              {!isSearching && <div className="text-sm font-medium text-muted-foreground mb-4" data-id="element-403">
                  Suggerimenti
                </div>}

              {/* Articoli */}
              {filteredArticles.length > 0 && <section data-id="element-404">
                  <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-405">
                    <FileText className="h-3.5 w-3.5" data-id="element-406" />
                    Articoli
                  </div>
                  <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-407">
                    {filteredArticles.map(article => <button key={article.id} onClick={() => {
                onClose();
                navigate(`/article/${article.id}`);
              }} className="w-full text-left px-5 py-4 hover:bg-muted/30 transition-colors flex items-center justify-between gap-4 group" data-id="element-408">
                        <div className="min-w-0 flex-1" data-id="element-409">
                          <div className="text-[15px] font-medium text-foreground truncate mb-1 group-hover:text-accent transition-colors" data-id="element-410">
                            {article.title}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2" data-id="element-411">
                            <span data-id="element-412">{article.source}</span>
                            <span data-id="element-413">·</span>
                            <span data-id="element-414">
                              {new Date(article.date).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right" data-id="element-415">
                          <div className="text-sm font-semibold text-accent" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-416">
                            {article.relevanceScore.toFixed(1)}
                          </div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider" data-id="element-417">
                            Score
                          </div>
                        </div>
                      </button>)}
                  </div>
                </section>}

              {/* Rassegne */}
              {filteredRassegne.length > 0 && <section data-id="element-418">
                  <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-419">
                    <Newspaper className="h-3.5 w-3.5" data-id="element-420" />
                    Rassegne
                  </div>
                  <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-421">
                    {filteredRassegne.map(rassegna => <button key={rassegna.id} onClick={() => {
                onClose();
                navigate(`/rassegne/${rassegna.id}`);
              }} className="w-full text-left px-5 py-4 hover:bg-muted/30 transition-colors flex items-center justify-between gap-4 group" data-id="element-422">
                        <div className="min-w-0 flex-1" data-id="element-423">
                          <div className="text-[15px] font-medium text-foreground truncate mb-1 group-hover:text-accent transition-colors" data-id="element-424">
                            {rassegna.name}
                          </div>
                          <div className="text-xs text-muted-foreground" data-id="element-425">
                            {rassegna.dateRange}
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right" data-id="element-426">
                          <div className="text-sm font-medium text-foreground" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-427">
                            {rassegna.articleCount}
                          </div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider" data-id="element-428">
                            Articoli
                          </div>
                        </div>
                      </button>)}
                  </div>
                </section>}

              {/* Reports */}
              {filteredReports.length > 0 && <section data-id="element-429">
                  <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-430">
                    <LayoutTemplate className="h-3.5 w-3.5" data-id="element-431" />
                    Report
                  </div>
                  <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-432">
                    {filteredReports.map(report => <button key={report.id} onClick={() => {
                onClose();
                navigate('/reports');
              }} className="w-full text-left px-5 py-4 hover:bg-muted/30 transition-colors flex items-center justify-between gap-4 group" data-id="element-433">
                        <div className="min-w-0 flex-1" data-id="element-434">
                          <div className="text-[15px] font-medium text-foreground truncate mb-1 group-hover:text-accent transition-colors" data-id="element-435">
                            {report.name}
                          </div>
                          <div className="text-xs text-muted-foreground" data-id="element-436">
                            {report.dateRange}
                          </div>
                        </div>
                        <div className="flex-shrink-0" data-id="element-437">
                          <Badge variant="outline" className={report.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'} data-id="element-438">
                            {report.status === 'completed' ? 'Completato' : 'Bozza'}
                          </Badge>
                        </div>
                      </button>)}
                  </div>
                </section>}

              {/* Testate */}
              {filteredSources.length > 0 && <section data-id="element-439">
                  <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-440">
                    <Building2 className="h-3.5 w-3.5" data-id="element-441" />
                    Testate
                  </div>
                  <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-442">
                    {filteredSources.map(source => <div key={source.name} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4" data-id="element-443">
                        <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-444">
                          <img src={source.logo} alt={source.name} className="w-8 h-8 rounded-md flex-shrink-0" data-id="element-445" />
                          <div className="text-[15px] font-medium text-foreground truncate" data-id="element-446">
                            {source.name}
                          </div>
                        </div>
                        <div className="flex-shrink-0" data-id="element-447">
                          <Badge className="text-[10px] px-2 py-0.5 font-normal bg-muted text-muted-foreground hover:bg-muted" data-id="element-448">
                            {tierLabels[source.tier] || source.tier}
                          </Badge>
                        </div>
                      </div>)}
                  </div>
                </section>}
            </div>}
        </div>
      </div>
    </motion.div>;
}
export function TopBar() {
  const [aiModeOpen, setAiModeOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  return <>
      <div className="hidden md:flex h-16 bg-card border-b border-border items-center justify-between px-6 flex-shrink-0" data-id="element-449">
        {/* Search + AI Mode */}
        <div className="flex items-center gap-3" data-id="element-450">
          <div className="relative" data-id="element-451">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-452" />
            <input type="text" placeholder="Cerca articoli, testate..." className="pl-9 pr-4 py-2 text-sm border border-input rounded-full bg-background w-64 focus:outline-none focus:ring-2 focus:ring-ring cursor-text" onClick={() => setSearchOpen(true)} onFocus={() => setSearchOpen(true)} readOnly data-id="element-453" />
          </div>
          <Button variant="outline" onClick={() => setAiModeOpen(true)} className="rounded-full h-9 px-4 text-sm border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-454">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" data-id="element-455" />
            AI Mode
          </Button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2" data-id="element-456">
          <Avatar className="h-9 w-9" data-id="element-457">
            <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-458" />
          </Avatar>
        </div>
      </div>

      {createPortal(<AnimatePresence data-id="element-459">
          {aiModeOpen && <AIModeOverlay onClose={() => setAiModeOpen(false)} data-id="element-460" />}
        </AnimatePresence>, document.body)}

      {createPortal(<AnimatePresence data-id="element-461">
          {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} data-id="element-462" />}
        </AnimatePresence>, document.body)}
    </>;
}